import '@naturehouse/design-system/components/atoms/date-field/DateField';
import type DateField from '@naturehouse/design-system/components/atoms/date-field/DateField';
import type TravelCompanyInput from '@naturehouse/design-system/components/atoms/travel-company-input/TravelCompanyInput';
import type {
    Observer,
    Subject
} from '@naturehouse/nh-essentials/lib/architecture/ObserverPattern';
import HttpClient from '@naturehouse/nh-essentials/lib/requests/HttpClient';
import defaultBaseDecimalSystem from '../../../common/Constants';
import GoogleAnalyticsEvent from '../../../common/GoogleAnalyticsEvents';
import TravelPeriodStateManager from '../../../common/search/travel-period/TravelPeriodStateManager';
import TravelPeriodUIManager from '../../../common/search/travel-period/TravelPeriodUIManager';
import Modal from '../../../ui/web-components/modal/Modal';
import { getCompanionData, getNumberOfNights } from '../../../util/dataLayer';
import { SelectedState } from '../../../common/search/travel-period/TravelPeriodStateManager.types';
import type DatePickerCalendar from '../../calendar/webComponents/DatePickerCalendar';
import { DatePickerCalendarEvents } from '../../calendar/webComponents/DatePickerCalendar';
import HouseDetailTrackingEvent from '../HouseDetailTracking';
import ClearCalendarHandler from './ClearCalendarHandler';
import DetailFormTracking from './DetailFormTracking';
import type DetailPriceList from './detail-price-list/DetailPriceList';
import './detail-price-list/DetailPriceList';

export enum BookingDataParameters {
    ARRIVAL_DATE = 'arrivalDate',
    DEPARTURE_DATE = 'departureDate'
}

export enum TravelPartyDataParameters {
    ADULTS = 'adults',
    CHILDREN = 'children',
    BABIES = 'babies',
    PETS = 'pets'
}

/**
 * @Deprecated An epic was planned to take the search features (travel party / dates etc),
 * and move these to the DS and completely refactor the website integration.
 * For this reason, this file is deprecated and should no longer be extended.
 * This file is also not unit tested and is a constant point of discussion.
 */
export default class DetailForm extends HTMLFormElement implements Observer {
    readonly #calendarModal: Modal;

    readonly #arrivalDateInput: HTMLInputElement = this.querySelector(
        'input[id="arrivalDate"]'
    ) as HTMLInputElement;

    readonly #departureDateInput: HTMLInputElement = this.querySelector(
        'input[id="departureDate"]'
    ) as HTMLInputElement;

    readonly #travelPartyInput: TravelCompanyInput | null = this.querySelector('nh-travel-company');

    readonly #travelPeriodStateManager = TravelPeriodStateManager.getInstance();

    readonly #travelPeriodUIManager = TravelPeriodUIManager;

    #travelPartyModal: Modal | null = null;

    #dataLayer: Record<string, string> = {};

    #destination = '';

    #validDepartureDatesRoute = '';

    #detailPriceList: DetailPriceList | null = null;

    #departureDates: string[] = [];

    readonly #departureDateField: DateField | null;

    readonly #datepickerCalendar: DatePickerCalendar;

    readonly #clearCalendarHandler: ClearCalendarHandler;

    readonly #detailFormTracking: DetailFormTracking = new DetailFormTracking(
        this as HTMLFormElement
    );

    constructor() {
        super();

        const template = this.querySelector('#datepicker-calendar-content') as HTMLTemplateElement;
        const headerTemplate = this.querySelector(
            '#calendar-input-modal-header'
        ) as HTMLTemplateElement;
        const footerTemplate = this.querySelector(
            '#calendar-input-modal-footer'
        ) as HTMLTemplateElement;

        if (!template || !footerTemplate || !headerTemplate) {
            throw new Error('Templates not found');
        }

        this.#travelPeriodStateManager.attach(this);

        this.#calendarModal = new Modal({
            title: template.dataset.title,
            classList: ['search-form-modal', 'nh-stay-duration'],
            hidden: true
        });

        this.#calendarModal.dataset.testid = 'datepicker-calendar-modal';
        this.#calendarModal.dataset.for = 'nh-stay-duration-detail-form';

        const header = document.importNode(headerTemplate.content, true);
        const content = document.importNode(template.content, true);
        const footer = document.importNode(footerTemplate.content, true);

        document.body.append(this.#calendarModal);

        this.#datepickerCalendar = content.querySelector(
            'datepicker-calendar'
        ) as DatePickerCalendar;

        const doneButton = footer.querySelector(
            '[data-role="close-modal"]'
        ) as HTMLButtonElement | null;

        doneButton?.addEventListener('click', this.#closeModal);

        this.#travelPeriodUIManager.dialog = this.#calendarModal;
        this.#travelPeriodUIManager.calendar = this.#datepickerCalendar;
        this.#travelPeriodUIManager.arrivalDateInput = this.#arrivalDateInput;
        this.#travelPeriodUIManager.departureDateInput = this.#departureDateInput;
        this.#travelPeriodUIManager.arrivalDateField = header.querySelector(
            'nh-date-field[data-name="arrivalDate"]'
        );

        this.#departureDateField = header.querySelector('nh-date-field[data-name="departureDate"]');
        this.#travelPeriodUIManager.departureDateField = this.#departureDateField;

        const arrivalDateValue = this.#travelPeriodUIManager.getArrivalDateValue();
        const departureDateValue = this.#travelPeriodUIManager.getDepartureDateValue();

        const arrivalDate = arrivalDateValue ? new Date(arrivalDateValue) : null;
        const departureDate = departureDateValue ? new Date(departureDateValue) : null;

        this.#travelPeriodStateManager.setInitialValues({
            arrivalDate: arrivalDate,
            departureDate: departureDate
        });

        this.#calendarModal.header = header;
        this.#calendarModal.content = content;
        this.#calendarModal.footer = footer;

        this.#clearCalendarHandler = new ClearCalendarHandler(
            this.#arrivalDateInput,
            this.#departureDateInput,
            this.#calendarModal,
            this.#datepickerCalendar
        );

        this.#clearCalendarHandler.initialize();
    }

    public async connectedCallback(): Promise<void> {
        const dataLayer = this.getAttribute('data-layer');
        if (dataLayer) {
            this.#dataLayer = JSON.parse(dataLayer);
        }

        this.#validDepartureDatesRoute = this.getAttribute('valid-departure-dates-route') ?? '';
        this.#destination = this.getAttribute('destination') ?? '';
        this.#detailPriceList = this.querySelector<DetailPriceList>('nh-detail-price-list');

        this.addEventListeners();

        if (this.#datepickerCalendar.selection.start && this.#departureDates.length === 0) {
            const queryParams = new URLSearchParams(window.location.search);
            await this.updateDepartureDates(queryParams, this.#datepickerCalendar);
        }

        setTimeout(() => {
            this.#travelPartyModal = document.querySelector(
                'nh-modal[data-for="travel-party"]'
            ) as Modal;
            if (
                this.#datepickerCalendar.selection.start &&
                this.#datepickerCalendar.selection.end
            ) {
                // If both dates are already selected, #handleDateRangeSelection is called.
                // So we should prevent calling the tracking function twice.
                return;
            }

            this.#trackOnUpdatePriceList();
        }, 1000);
    }

    #updateAndTrackPriceList(): void {
        this.#closeModal();

        if (!this.#detailPriceList) {
            return;
        }

        this.#detailPriceList.render().then(this.#trackOnUpdatePriceList);
    }

    public async update(subject: Subject): Promise<void> {
        if (!(subject instanceof TravelPeriodStateManager)) {
            return;
        }

        const { activeSelectedState } = subject;

        if (activeSelectedState !== SelectedState.DEPARTURE) {
            return;
        }

        this.#datepickerCalendar.allowedDates = this.#departureDates;
    }

    protected addEventListeners(): void {
        this.addEventListener('submit', this.#handleFormSubmit.bind(this));

        this.#travelPartyInput?.addEventListener(
            'change',
            this.#handleTravelPartyChange.bind(this)
        );

        this.#arrivalDateInput.addEventListener('clear', this.#handleArrivalCleared);
        this.#departureDateInput.addEventListener('clear', this.#handleDepartureCleared);
        this.#departureDateField?.addEventListener('clear', this.#handleDepartureCleared);

        this.#datepickerCalendar.addEventListener(
            DatePickerCalendarEvents.DATE_RANGE_SELECTED,
            this.#handleDateRangeSelection
        );
    }

    #handleTravelPartyChange(): void {
        this.#updateAndTrackPriceList();

        const travelParty = this.#getTravelPartyData();
        const queryParams = new URLSearchParams(window.location.search);
        for (const key in travelParty) {
            if (Object.prototype.hasOwnProperty.call(travelParty, key)) {
                // @ts-ignore
                const value = travelParty[key];

                if (!value) {
                    queryParams.delete(key);
                    continue;
                }

                if (value.toString() === queryParams.get(key)?.toString()) {
                    continue;
                }

                queryParams.set(key, value.toString());
            }
        }

        this.#updateQueryParams(queryParams);

        GoogleAnalyticsEvent.trackEventWithDelay<GoogleAnalytics4TravelPartySearchEvent>(
            HouseDetailTrackingEvent.TRAVEL_PARTY_SELECTION,
            {
                page: 'house-detail',
                ...travelParty
            }
        );
    }

    #handleDateSelectionTracking(selection: DateRangeString): void {
        if (selection.start === null) {
            return;
        }

        if (selection.end === null) {
            GoogleAnalyticsEvent.trackEvent<GoogleAnalytics4SelectDateEvent>(
                HouseDetailTrackingEvent.ARRIVAL_DATE_SELECTION,
                {
                    date: new Date(selection.start)
                }
            );
            return;
        }

        GoogleAnalyticsEvent.trackEvent<GoogleAnalytics4SelectDateEvent>(
            HouseDetailTrackingEvent.DEPARTURE_DATE_SELECTION,
            {
                date: new Date(selection.end)
            }
        );
    }

    readonly #handleDateRangeSelection = async (e: Event): Promise<void> => {
        const calendar = e.target as DatePickerCalendar;

        const event: CustomEvent = e as CustomEvent;
        const arrivalDate: Date | null = event.detail.start;
        const departureDate: Date | null = event.detail.end;
        const firstUpdate: boolean = event.detail.firstUpdate ?? false;

        if (!firstUpdate) {
            this.#travelPeriodStateManager.update({ arrivalDate, departureDate });
        }

        const selection = calendar.selection;
        this.#handleDateSelectionTracking(selection);

        if (selection.start === null) {
            return;
        }

        const queryParams = new URLSearchParams(window.location.search);
        queryParams.set(BookingDataParameters.ARRIVAL_DATE, selection.start);

        if (selection.end) {
            this.#updateAndTrackPriceList();
            queryParams.set(BookingDataParameters.DEPARTURE_DATE, selection.end);
        } else {
            queryParams.delete(BookingDataParameters.DEPARTURE_DATE);
        }

        this.#updateQueryParams(queryParams);

        calendar.allowedDates = [];
        await this.updateDepartureDates(queryParams, calendar);
    };

    #updateQueryParams(queryParams: URLSearchParams): void {
        const searchString = queryParams.toString();

        if (searchString === new URLSearchParams(window.location.search).toString()) {
            return;
        }

        const newPath = `${window.location.pathname}?${searchString}`;
        window.history.replaceState(null, '', newPath);
    }

    readonly #handleArrivalCleared = (): void => {
        this.#detailPriceList?.clear();

        const queryParams = new URLSearchParams(window.location.search);
        queryParams.delete(BookingDataParameters.ARRIVAL_DATE);
        queryParams.delete(BookingDataParameters.DEPARTURE_DATE);
        this.#updateQueryParams(queryParams);
    };

    readonly #handleDepartureCleared = (): void => {
        this.#detailPriceList?.clear();

        const queryParams = new URLSearchParams(window.location.search);
        queryParams.delete(BookingDataParameters.DEPARTURE_DATE);
        this.#updateQueryParams(queryParams);
    };

    protected updateDepartureDates(
        queryParams: StandardObjectInterface,
        calendar: DatePickerCalendar
    ): Promise<void> {
        return HttpClient.get(`${this.#validDepartureDatesRoute}?${queryParams.toString()}`).then(
            (response: unknown): void => {
                const parsedResponse = response as StandardObjectInterface;
                if (parsedResponse && parsedResponse.departureDates) {
                    calendar.allowedDates = parsedResponse.departureDates;
                    this.#departureDates = parsedResponse.departureDates;
                }
            }
        );
    }

    #handleFormSubmit(event: Event): void {
        event.preventDefault();

        const dateRange = this.#datepickerCalendar.selectedDateRange;

        if (!dateRange.start || !dateRange.end) {
            this.#travelPeriodUIManager.openDialog(this.#arrivalDateInput);
            return;
        }

        this.#detailFormTracking.processAddToCartData(
            this.#dataLayer,
            getNumberOfNights(dateRange.start, dateRange.end),
            this.#datepickerCalendar.selection
        );

        this.submit();
    }

    #getTravelPartyData(): TravelPartyDataInterface {
        if (!this.#travelPartyModal) {
            return this.#getTravelPartyDataFromUrl();
        }
        return getCompanionData(this.#travelPartyModal);
    }

    readonly #closeModal = (): void => {
        if (!this.#calendarModal.isOpen) {
            return;
        }

        this.#calendarModal.close();
    };

    #getTravelPartyDataFromUrl(): TravelPartyDataInterface {
        const urlParams = new URLSearchParams(window.location.search);
        return {
            adults: parseInt(
                urlParams.get(TravelPartyDataParameters.ADULTS) || '0',
                defaultBaseDecimalSystem
            ),
            children: parseInt(
                urlParams.get(TravelPartyDataParameters.CHILDREN) || '0',
                defaultBaseDecimalSystem
            ),
            babies: parseInt(
                urlParams.get(TravelPartyDataParameters.BABIES) || '0',
                defaultBaseDecimalSystem
            ),
            pets: parseInt(
                urlParams.get(TravelPartyDataParameters.PETS) || '0',
                defaultBaseDecimalSystem
            )
        };
    }

    readonly #trackOnUpdatePriceList = (): void => {
        const companionData: TravelPartyDataInterface = this.#getTravelPartyData();
        this.#detailFormTracking.trackDetailPageImpressionSqueezely(
            companionData,
            this.#datepickerCalendar.selection,
            this.#destination
        );

        const dateRange = this.#datepickerCalendar.selectedDateRange;
        const quantity =
            dateRange.start && dateRange.end
                ? getNumberOfNights(dateRange.start, dateRange.end)
                : 1;

        this.#detailFormTracking.trackDetailPageImpression(quantity);
    };
}

if (!customElements.get('detail-form')) {
    customElements.define('detail-form', DetailForm, { extends: 'form' });
}
